import React, { useState } from 'react';
import { formatNoticeDate } from '@/features/centerNotice/formatDate';
import Modal from '@/components/modal/Modal';
import { DemiLightText, RegularText } from '@/lib/Text';
import { CenterNotice } from '@/features/centerNotice/types';
import ImageViewerModal from '@/components/modal/ImageViewerModal';
import ActionButton from './ActionButton';

interface ModalNoticeDetailProps {
    notice: CenterNotice;
    onClose: () => void;
    onEdit?: (notice: CenterNotice) => void;
    onDuplicate?: (notice: CenterNotice) => void;
    onDelete?: (notice: CenterNotice) => void;
}

const ModalNoticeDetail = ({ notice, onClose, onEdit, onDuplicate, onDelete }: ModalNoticeDetailProps) => {
    const displayDate = formatNoticeDate(notice, 'YYYY-MM-DD(dd)');
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    return (
        <Modal
            whiteViewWidth={820}
            isCloseButton
            onClose={onClose}
            borderRadius={37}
            mt={91}
            mb={150}
            isWhiteOverFlowScroll={false}
        >
            <div className="flex flex-col p-[40px] pb-[30px]">
                {/* Header */}
                <div className="flex flex-col gap-[12px] pr-[40px] ">
                    <RegularText fontSize={22} lineHeight={28} color="#191F28" letterSpacing={-0.5}>
                        {notice.title}
                    </RegularText>
                    <DemiLightText fontSize={15} lineHeight={22} color="#8B9194" letterSpacing={-0.3}>
                        {displayDate}
                    </DemiLightText>
                </div>

                {/* Divider */}
                <div className="w-full h-[0.5px] bg-[#E0E0E0] mt-[24px] mb-[28px]" />

                {/* Images */}
                {notice.imageUrls.length > 0 && (
                    <div className="flex flex-col gap-[12px] mb-[28px]">
                        {notice.imageUrls.map((url, index) => (
                            <div
                                key={index}
                                className="w-full max-h-[314px] bg-black  flex items-center justify-center overflow-hidden cursor-pointer"
                                onClick={() => {
                                    setCurrentImageIndex(index);
                                    setIsViewerOpen(true);
                                }}
                            >
                                <img
                                    src={url}
                                    alt={`공지 이미지 ${index + 1}`}
                                    className="max-w-full max-h-[314px] object-contain"
                                />
                            </div>
                        ))}
                    </div>
                )}

                {/* Content */}
                <div className="whitespace-pre-wrap mb-[32px]">
                    <DemiLightText fontSize={16} lineHeight={32} color="#333D4B" letterSpacing={-0.4}>
                        {notice.content}
                    </DemiLightText>
                </div>

                {/* Bottom Divider */}
                <div className="w-full h-[0.5px] bg-[#E0E0E0] mb-[20px]" />

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-[12px]">
                    {notice.status !== 'scheduled' && (
                        <ActionButton
                            iconName="SvgEdit"
                            label="수정"
                            onClick={() => onEdit?.(notice)}
                            strokeWidth={1}
                        />
                    )}
                    {notice.status !== 'scheduled' && (
                        <ActionButton
                            iconName="SvgCopy"
                            label="복제"
                            onClick={() => onDuplicate?.({ ...notice, title: `[복제] ${notice.title}` })}
                            strokeWidth={1}
                        />
                    )}
                    <ActionButton iconName="SvgTrash" label="삭제" onClick={() => onDelete?.(notice)} isFill />
                </div>
            </div>
            <ImageViewerModal
                images={notice.imageUrls}
                initialIndex={currentImageIndex}
                isOpen={isViewerOpen}
                onClose={() => setIsViewerOpen(false)}
            />
        </Modal>
    );
};

export default ModalNoticeDetail;
